package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.components.layouts.PageLayout
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.brand.Crest
import com.sddamico.swigmore.ds.brand.CrestVariant
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.TextField
import com.sddamico.swigmore.ds.forms.TextFieldVariant
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.models.AuthRules
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.queryParam
import com.sddamico.swigmore.util.rememberAction
import com.sddamico.swigmore.util.rememberAuthState
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.w3c.dom.url.URL
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Email, then the emailed code. `?next=` is where to land afterwards. */
@Page
@Composable
fun SigninPage() {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()
  val next = remember { safeNext(queryParam("next")) }
  val signedIn = (auth.value as? LoadState.Ready)?.value != null
  // Replacing /signin in history, so Back from the destination doesn't bounce off it again.
  LaunchedEffect(signedIn) { if (signedIn) ctx.router.navigateTo(next, UpdateHistoryMode.REPLACE) }

  PageLayout("Sign in", showAdmin = false) {
    Div(attrs = Modifier.css("padding" to "56px 20px 72px", "display" to "flex", "justify-content" to "center").toAttrs()) {
      Card(
        Modifier.css("width" to "100%", "max-width" to "400px", "display" to "flex", "flex-direction" to "column",
          "gap" to "16px"),
        padding = "30px 26px", radius = "14px", stitchInset = "8px",
      ) {
        Crest(variant = CrestVariant.Field, size = 54)
        SigninSteps(onVerified = {
          auth.refresh()
          ctx.router.navigateTo(next, UpdateHistoryMode.REPLACE)
        })
      }
    }
  }
}

@Composable
private fun SigninSteps(onVerified: suspend () -> Unit) {
  val request = rememberAction()
  var email by remember { mutableStateOf("") }
  var code by remember { mutableStateOf("") }
  var codeSent by remember { mutableStateOf(false) }
  var sentTo by remember { mutableStateOf<String?>(null) }
  var resendIn by remember { mutableStateOf(0) }

  LaunchedEffect(resendIn > 0) {
    while (resendIn > 0) {
      delay(1000)
      resendIn--
    }
  }

  fun sendCode() {
    val to = email.trim()
    // The code already sent to this address is still good, and the server would refuse another one yet.
    if (to == sentTo && resendIn > 0) {
      codeSent = true
      return
    }
    request.run {
      PollApi.sendOtp(to)
      code = ""
      sentTo = to
      codeSent = true
      resendIn = AuthRules.RESEND_COOLDOWN_SECONDS
    }
  }

  fun verify() {
    if (code.length != AuthRules.OTP_LENGTH) return
    request.run {
      PollApi.verify(email.trim(), code)
      onVerified()
    }
  }

  Div(attrs = Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "14px").toAttrs()) {
    if (!codeSent) {
      Heading("Suit up.") {
        Text("Drop your email and we'll send a ${AuthRules.OTP_LENGTH}-digit code. No passwords. Codes only go to owners on the league list.")
      }
      TextField(email, { email = it }, placeholder = "you@example.com", type = "email", label = "Email",
        onEnter = { if (email.isNotBlank()) sendCode() })
      Button("Send My Code", fullWidth = true, disabled = request.busy || email.isBlank(), onClick = { sendCode() })
    } else {
      Heading("Check the mailbag.") {
        Text("If ")
        B { Text(email.trim()) }
        Text(" is on the league list, a code is on its way. It expires in ${AuthRules.OTP_EXPIRY_MINUTES} minutes.")
      }
      TextField(code, {
        // Submit on the last digit, typed or pasted, but not again when an extra keystroke is dropped.
        val completed = it.length == AuthRules.OTP_LENGTH && it != code
        code = it
        if (completed) verify()
      }, variant = TextFieldVariant.Code, placeholder = "0".repeat(AuthRules.OTP_LENGTH), label = "${AuthRules.OTP_LENGTH}-digit code",
        onEnter = { verify() })
      Button("Verify & Enter", fullWidth = true, disabled = request.busy || code.length != AuthRules.OTP_LENGTH, onClick = { verify() })
      Div(attrs = Modifier.typeMono("11px").css("display" to "flex", "justify-content" to "space-between",
        "color" to SwColor.inkSecondary).toAttrs()) {
        Link("← WRONG EMAIL") { codeSent = false; request.error = null }
        if (resendIn > 0) {
          Span { Text("RESEND IN ${resendIn / 60}:${(resendIn % 60).toString().padStart(2, '0')}") }
        } else {
          Link("RESEND CODE") { sendCode() }
        }
      }
    }
    request.error?.let { message ->
      Div(attrs = Modifier.typeBody("13px", 600, "1.4").css("color" to SwColor.ink).toAttrs { attr("role", "alert") }) {
        Text(message)
      }
    }
  }
}

@Composable
private fun Heading(title: String, lede: @Composable () -> Unit) {
  Div {
    Div(attrs = Modifier.typeDisplay("28px", "1.05").toAttrs()) { Text(title) }
    Div(attrs = Modifier.typeBody("14px", lineHeight = "1.5").css("opacity" to ".8", "margin-top" to "8px").toAttrs()) {
      lede()
    }
  }
}

@Composable
private fun Link(label: String, onClick: () -> Unit) {
  Span(attrs = Modifier.css("cursor" to "pointer").toAttrs {
    attr("role", "button")
    attr("tabindex", "0")
    onClick { onClick() }
    onKeyDown { if (it.key == "Enter" || it.key == " ") { it.preventDefault(); onClick() } }
  }) { Text(label) }
}

/** Only same-origin destinations, so a crafted `?next=` can't send a fresh session off-site. */
private fun safeNext(next: String?): String {
  val url = runCatching { URL(next ?: "/", window.location.origin) }.getOrNull()
  return if (url?.origin == window.location.origin) url.pathname + url.search + url.hash else "/"
}
